import PropTypes from "prop-types";
import styled, { css } from "styled-components";
import { space, typography, layout } from "styled-system";
import propTypes from "@styled-system/prop-types";
import themeGet from "@styled-system/theme-get";
import Link from "../../../hoc/Link";
import { BadgeStyle } from "../badge/badge.style";

const HeaderStyle = styled("header", {
  fixed: PropTypes.bool,
  ...propTypes.space,
  ...propTypes.layout,
  ...propTypes.typography,
})`
  position: relative;
  display: flex;
  flex-direction: row;
  flex-wrap: wrap;
  flex-shrink: 0;
  min-height: 56px;
  background: ${themeGet("colors.header.backgroundColor", "#fff")};
  border-bottom: 1px solid ${themeGet("colors.header.borderColor", "#d8dbe0")};
  
  ${({ fixed }) => fixed && css`
    position: fixed;
    right: 0;
    left: 0;
    z-index: 1029;
  `}
  
  /* Style system support */
  ${space}
  ${layout}
  ${typography}
`;

const HeaderNavStyle = styled("ul", {
  ...propTypes.space,
  ...propTypes.layout,
  ...propTypes.typography,
})`
  display: flex;
  flex-direction: row;
  align-items: center;
  min-height: 56px;
  padding: 0;
  margin-bottom: 0;
  list-style: none;

  /* Style system support */
  ${space}
  ${layout}
  ${typography}
`;

const HeaderNavItemStyle = styled("li", {
  ...propTypes.space,
  ...propTypes.layout,
  ...propTypes.typography,
})`
  ${HeaderNavStyle} & {
    position: relative;
  }

  /* Style system support */
  ${space}
  ${layout}
  ${typography}
`;

const HeaderNavLinkStyle = styled(Link, {
  disabled: PropTypes.bool,
  ...propTypes.space,
  ...propTypes.layout,
  ...propTypes.typography,
})`
  ${HeaderNavStyle} & {
    display: flex;
    align-items: center;
    padding-right: 8px;
    padding-left: 8px;
    color: ${themeGet("colors.headerNavLink.color", "rgba(0, 0, 21, 0.5)")};
    
    &[aria-current] {
      color: ${themeGet("colors.headerNavLink.colorActive", "rgba(0, 0, 21, 0.9)")};
    }
    
    ${BadgeStyle} {
      position: absolute;
      top: 50%;
      left: 50%;
      margin-top: -16px;
      margin-left: 0;
    }
    
    &:hover {
      text-decoration: none;
    }
    
    &:hover,
    &:focus {
      color: ${themeGet("colors.headerNavLink.colorHover", "rgba(0, 0, 21, 0.7)")};
    }
    
    ${({ disabled }) => disabled && css`
      color: ${themeGet("colors.headerNavLink.colorHover", "rgba(0, 0, 21, 0.3)")};
    `}
  }

  /* Style system support */
  ${space}
  ${layout}
  ${typography}
`;

const HeaderBrandCenteredStyle = css`
  position: absolute;
  top: 56px;
  left: 50%;
  transform: translate(-50%, -100%);
`;
const HeaderBrandStyle = styled(Link, {
  centered: PropTypes.oneOfType([
    PropTypes.bool,
    PropTypes.oneOf([
      "xSmall",
      "smallDown",
      "smallUp",
      "mediumDown",
      "mediumUp",
      "largeDown",
      "largeUp",
      "xLargeDown",
      "xLargeUp",
      "xxLargeDown",
      "xxLarge",
    ]),
  ]),
  ...propTypes.space,
  ...propTypes.layout,
  ...propTypes.typography,
})`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: auto;
  min-height: 56px;
  transition: width 0.3s;
  
  ${({ centered }) => ((typeof centered === "boolean" && centered) || ["xSmall", "xxLargeDown"].includes(centered)) && css`
    ${HeaderBrandCenteredStyle}
  `}
  
  ${themeGet("mediaQueries.smallDown")} {
    ${({ centered }) => centered === "smallDown" && css`
      ${HeaderBrandCenteredStyle}
    `}
  }

  ${themeGet("mediaQueries.smallUp")} {
    ${({ centered }) => centered === "smallUp" && css`
      ${HeaderBrandCenteredStyle}
    `}
  }

  ${themeGet("mediaQueries.mediumDown")} {
    ${({ centered }) => centered === "mediumDown" && css`
      ${HeaderBrandCenteredStyle}
    `}
  }

  ${themeGet("mediaQueries.mediumUp")} {
    ${({ centered }) => centered === "mediumUp" && css`
      ${HeaderBrandCenteredStyle}
    `}
  }

  ${themeGet("mediaQueries.largeDown")} {
    ${({ centered }) => centered === "largeDown" && css`
      ${HeaderBrandCenteredStyle}
    `}
  }

  ${themeGet("mediaQueries.largeUp")} {
    ${({ centered }) => centered === "largeUp" && css`
      ${HeaderBrandCenteredStyle}
    `}
  }

  ${themeGet("mediaQueries.xLargeDown")} {
    ${({ centered }) => centered === "xLargeDown" && css`
      ${HeaderBrandCenteredStyle}
    `}
  }

  ${themeGet("mediaQueries.xLargeUp")} {
    ${({ centered }) => centered === "xLargeUp" && css`
      ${HeaderBrandCenteredStyle}
    `}
  }

  ${themeGet("mediaQueries.xxLarge")} {
    ${({ centered }) => centered === "xxLarge" && css`
      ${HeaderBrandCenteredStyle}
    `}
  }

  ${HeaderStyle} & {
    color: ${themeGet("colors.headerBrand.color", "#4f5d73")};
    background-color: ${themeGet("colors.headerBrand.backgroundColor", "transparent")};
    
    &:hover,
    &:focus {
      color: ${themeGet("colors.headerBrand.colorHover", "#3a4555")};
    }
  }

  /* Style system support */
  ${space}
  ${layout}
  ${typography}
`;

const MobileHeaderNavStyle = styled("div", {
  ...propTypes.space,
  ...propTypes.layout,
  ...propTypes.typography,
})`
  position: fixed;
  top: 57px;
  width: 100%;
  height: calc(100vh - 57px);
  background: #fff;
  padding: 30px 15px;
  display: block;
  font-size: 24px;
  z-index: 1000;

  /* Style system support */
  ${space}
  ${layout}
  ${typography}
`;

export {
  HeaderStyle,
  HeaderNavStyle,
  HeaderNavItemStyle,
  HeaderNavLinkStyle,
  HeaderBrandStyle,
  MobileHeaderNavStyle,
};
